import { api } from "~/utils/api"
import type { ICollectionDetails } from "~/hooks/useCollections"
import { useSkuStore } from "~/providers/sku-store-provider"
import { runPartialRefresh } from "~/utils/cacheUtils/runPartialRefresh"
import { sortCollectionsByStartThenName } from "~/utils/collectionSort"

/**
 * Merge updated collection details into existing cache.
 * Forces new object references for AG-Grid rerendering.
 */
export function mergeCollectionsIntoCache(
  existingData: ICollectionDetails[] | undefined,
  updatedData: ICollectionDetails[] | undefined,
): ICollectionDetails[] {
  if (!existingData)
    return Array.isArray(updatedData)
      ? updatedData.map((c) => ({ ...c, variantIds: [...(c.variantIds ?? [])] }))
      : []
  if (!updatedData || updatedData.length === 0) return existingData

  const collectionMap = new Map<number, ICollectionDetails>()

  // Add existing data — create new references
  for (const collection of existingData) {
    collectionMap.set(collection.id, {
      ...collection,
      variantIds: [...(collection.variantIds ?? [])],
    })
  }

  // Merge updated collections — create new references
  for (const updatedCollection of updatedData) {
    collectionMap.set(updatedCollection.id, {
      ...updatedCollection,
      variantIds: updatedCollection.variantIds ? [...updatedCollection.variantIds] : [],
      collectionGroup: updatedCollection.collectionGroup
        ? { ...updatedCollection.collectionGroup }
        : null,
    })
  }

  // Return new array sorted to match backend order (startDate asc, then name)
  return Array.from(collectionMap.values()).sort(sortCollectionsByStartThenName)
}

/**
 * Partial collection cache refresh
 */
async function refreshCollectionsCache(
  utils: ReturnType<typeof api.useUtils>,
  collectionIds: number[],
  warehouseId?: string,
  channel?: string,
) {
  if (!collectionIds || collectionIds.length === 0) return

  await runPartialRefresh({
    cancel: () => {
      void utils.collection.getAllWithDetails.cancel({ warehouseId, channel })
    },
    fetch: () =>
      utils.collection.getCollectionDetailsByIds.fetch(
        { collectionIds, warehouseId, channel },
        { staleTime: 0 },
      ),
    apply: (updatedCollections) => {
      if (updatedCollections.length === 0) return
      utils.collection.getAllWithDetails.setData(
        { warehouseId, channel },
        (oldData: ICollectionDetails[] | undefined) => {
          const merged = mergeCollectionsIntoCache(oldData, updatedCollections)
          if (!merged || merged.length === 0) {
            console.warn("Collection cache merge returned empty data, keeping old data")
            return oldData
          }
          return merged
        },
      )
    },
    fallback: () => {
      void utils.collection.getAllWithDetails.invalidate({ warehouseId, channel })
    },
  })

  // getAllWithDetails is cached per option combo, and getCollectionDetailsByIds can only
  // produce the { warehouseId, channel } one — preferDemandPlan/filterOutVariants change the
  // forecast numbers, so those entries (e.g. /demand's) must refetch instead of being patched.
  void utils.collection.getAllWithDetails.invalidate(undefined, {
    predicate: ({ queryKey }) => {
      const input = queryKey[1]?.input
      return (
        input?.warehouseId !== warehouseId ||
        input?.channel !== channel ||
        input?.filterOutVariants === true ||
        input?.preferDemandPlan === true
      )
    },
  })
}

/**
 * Hook to refresh partial collection cache
 */
export function usePartialRefreshCollections() {
  const utils = api.useUtils()
  const warehouseId = useSkuStore((state) => state.warehouseId)
  const channel = useSkuStore((state) => state.channel)

  return async (collectionIds: number[]) => {
    await refreshCollectionsCache(utils, collectionIds, warehouseId, channel)
  }
}

/**
 * Filter out deleted collection IDs from existing cache data.
 */
function removeCollectionsFromCache(
  existingData: ICollectionDetails[] | undefined,
  collectionIds: number[],
): ICollectionDetails[] | undefined {
  if (!existingData) return existingData
  const idsToRemove = new Set(collectionIds)
  return existingData.filter((c) => !idsToRemove.has(c.id))
}

/**
 * Hook to remove collections from cache by ID (for delete operations).
 */
export function useRemoveCollectionsFromCache() {
  const utils = api.useUtils()
  const warehouseId = useSkuStore((state) => state.warehouseId)
  const channel = useSkuStore((state) => state.channel)

  return (collectionIds: number[]): string[] => {
    if (!collectionIds.length) return []

    const currentData = utils.collection.getAllWithDetails.getData({ warehouseId, channel })
    const idsToRemove = new Set(collectionIds)
    const affectedVariantIds = (currentData ?? [])
      .filter((c) => idsToRemove.has(c.id))
      .flatMap((c) => c.variantIds ?? [])

    void utils.collection.getAllWithDetails.cancel({ warehouseId, channel })
    utils.collection.getAllWithDetails.setData({ warehouseId, channel }, (oldData) =>
      removeCollectionsFromCache(oldData, collectionIds),
    )

    return [...new Set(affectedVariantIds)]
  }
}
